import { useCallback } from 'react';
import { MessageType, SessionType } from 'models/Message';
import { AvatarColors, User } from 'models/User';
import { XOR } from 'types';
import { getIsCurrentUser, getMemberInitials } from 'utils';

const SELL_REPRESENTATIVE_KEY = 'sell-representative';

type MessageAvatar = XOR<
  { user: Partial<User> },
  { imageUrl?: string; initials?: string; color?: AvatarColors }
>;

export type MessageIdentity = {
  isMyMessage: boolean;
  senderKey: string;
  avatar: MessageAvatar;
};

/**
 * Derives whether a message belongs to the current user (right/blue) and which
 * avatar represents its sender. The side depends on the viewer, the avatar only
 * on the sender — sell-side senders are always shown as the company.
 */
export default function useChatMessageIdentity(session: SessionType) {
  const { isRepresentingSellSide, sellRepresentative } = session || {};

  return useCallback(
    (
      message: Pick<MessageType, 'sentBy' | 'isSentBySellRepresentative'>,
    ): MessageIdentity => {
      const { sentBy, isSentBySellRepresentative } = message;

      const isMyMessage = isRepresentingSellSide
        ? !!isSentBySellRepresentative
        : getIsCurrentUser(sentBy?._id);

      const senderKey = isSentBySellRepresentative
        ? SELL_REPRESENTATIVE_KEY
        : sentBy?._id;

      const avatar: MessageAvatar = isSentBySellRepresentative
        ? {
            imageUrl: sellRepresentative?.companyLogo?.url,
            initials: getMemberInitials(sellRepresentative?.companyName),
          }
        : { user: sentBy ?? {} };

      return { isMyMessage, senderKey, avatar };
    },
    [isRepresentingSellSide, sellRepresentative],
  );
}
